import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "../../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../../config/api";
import VideoLibrary from "./VideoLibrary";
import { parsePublicationStream, PUBLICATION_STAGES, publicationErrorMessage } from "./publicationStream";

// Status badge per URL row
const STATUS_META = {
    idle: {
        dot: "bg-gray-300",
        text: "text-gray-400",
        label: { en: "Queued", zh: "排隊中" },
    },
    loading: {
        dot: "bg-orange-400 animate-pulse",
        text: "text-orange-500",
        label: { en: "Processing…", zh: "處理中…" },
    },
    success: {
        dot: "bg-green-400",
        text: "text-green-600",
        label: { en: "Done", zh: "完成" },
    },
    duplicate: {
        dot: "bg-blue-400",
        text: "text-blue-600",
        label: { en: "Already exists", zh: "已存在" },
    },
    review: {
        dot: "bg-yellow-400",
        text: "text-yellow-700",
        label: { en: "Needs review", zh: "待審核" },
    },
    error: {
        dot: "bg-red-400",
        text: "text-red-500",
        label: { en: "Failed", zh: "失敗" },
    },
};

function UrlRow({ item, language, onRemove }) {
    const meta = STATUS_META[item.status];
    return (
        <div
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all
            ${
                item.status === "success"
                    ? "bg-green-50 border-green-200"
                    : item.status === "duplicate"
                      ? "bg-blue-50 border-blue-200"
                      : item.status === "error"
                        ? "bg-red-50 border-red-200"
                        : item.status === "loading"
                          ? "bg-orange-50 border-orange-200"
                          : "bg-gray-50 border-gray-200"
            }`}
        >
            {/* Status dot */}
            <span
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${meta.dot}`}
            />

            {/* URL */}
            <span
                className="flex-1 text-sm text-gray-700 truncate min-w-0"
                title={item.url}
            >
                {item.url}
            </span>

            {/* Status label / error */}
            <span
                className={`text-xs font-semibold ${["error", "loading"].includes(item.status) ? "min-w-0 max-w-[65%] break-words" : "flex-shrink-0"} ${meta.text}`}
            >
                {item.status === "error" && item.error
                    ? item.error
                    : item.status === "loading" && PUBLICATION_STAGES[item.stage] ?
                        PUBLICATION_STAGES[item.stage][language === "zh" ? 1 : 0] : meta.label[language === "zh" ? "zh" : "en"]}
            </span>

            {/* Spinner or remove button */}
            {item.status === "loading" ? (
                <div className="w-4 h-4 border-2 border-orange-300 border-t-orange-500 rounded-full animate-spin flex-shrink-0" />
            ) : item.status === "idle" ? (
                <button
                    onClick={() => onRemove(item.id)}
                    className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            ) : (
                <span className="w-4 flex-shrink-0" /> /* spacer */
            )}
        </div>
    );
}

let _idCounter = 0;
const makeItem = (url) => ({
    id: ++_idCounter,
    url: url.trim(),
    status: "idle",
    error: null,
});

function PublishVideos({ language, onBack }) {
    const { currentUser } = useAuth();

    const [inputValue, setInputValue] = useState("");
    const [queue, setQueue] = useState([]); // list of { id, url, status, error }
    const [isRunning, setIsRunning] = useState(false);
    const [libraryState, setLibraryState] = useState({ revision: 0, addedIds: [] });
    const abortRef = useRef(false);
    const requestAbortRef = useRef(null);
    useEffect(() => () => { abortRef.current = true; requestAbortRef.current?.abort(); }, []);

    const t = (en, zh) => (language === "zh" ? zh : en);

    // ── Add URLs from the text area ──────────────────────────────────────────
    const handleAdd = () => {
        const url = inputValue.trim();
        if (!url) return;
        if (queue.some((q) => q.url === url)) {
            setInputValue("");
            return;
        }
        setQueue((prev) => [...prev, makeItem(url)]);
        setInputValue("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAdd();
        }
    };

    const handleRemove = (id) => {
        setQueue((prev) => prev.filter((item) => item.id !== id));
    };

    const handleClearDone = () => {
        setQueue((prev) => prev.filter((item) => item.status === "idle"));
    };

    // ── Process queue sequentially ───────────────────────────────────────────
    const handleStart = async () => {
        const pending = queue.filter((item) => item.status === "idle");
        if (!pending.length) return;

        setIsRunning(true);
        abortRef.current = false;

        for (const item of pending) {
            if (abortRef.current) break;

            // Mark as loading
            setQueue((prev) =>
                prev.map((q) =>
                    q.id === item.id ? { ...q, status: "loading" } : q,
                ),
            );

            try {
                const controller = new AbortController();
                requestAbortRef.current = controller;
                const token = await currentUser.getIdToken(true);
                const res = await axios.post(
                    `${getApiUrl()}/api/video/upload`,
                    { video_url: item.url, stream: true },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: controller.signal,
                        timeout: 31 * 60_000,
                        responseType: "text",
                        transformResponse: [(data) => data],
                        onDownloadProgress: (event) => {
                            const text = event.event?.target?.responseText;
                            if (typeof text !== "string") return;
                            try {
                                const { stage } = parsePublicationStream(text);
                                if (stage) setQueue((prev) => prev.map((q) => q.id === item.id ? { ...q, stage } : q));
                            } catch (error) {
                                // Final parsing reports incomplete/malformed responses as failures.
                            }
                        },
                    },
                );

                let result = res.data;
                if (typeof result === "string") {
                    const stream = parsePublicationStream(result, true);
                    if (stream.error) {
                        const error = new Error(stream.error.error);
                        error.response = { data: stream.error, status: stream.error.status };
                        throw error;
                    }
                    result = stream.result;
                }
                const isExisting = result?.result === "existing";
                const isPublished = result?.result === "published";
                if (
                    (!isExisting && !isPublished) ||
                    !result.video_id ||
                    !(isExisting ? ["active", "needs_review", "inactive"] : ["active", "needs_review"]).includes(result.status) ||
                    (result.status === "active" && !(result.chunk_count > 0) && !(isExisting && result.content_scope === "title_only" && result.chunk_count === 0))
                ) {
                    throw new Error(t("The publisher did not confirm publication.", "發布服務尚未確認上架成功。"));
                }
                setQueue((prev) =>
                    prev.map((q) =>
                        q.id === item.id
                            ? {
                                  ...q,
                                  status: result.status === "needs_review" ? "review" : isExisting ? "duplicate" : "success",
                              }
                            : q,
                    ),
                );
                setLibraryState((prev) => ({
                    revision: prev.revision + 1,
                    addedIds: isPublished ? [...new Set([...prev.addedIds, result.video_id])] : prev.addedIds,
                }));
            } catch (err) {
                const msg =
                    (err.code === "ERR_CANCELED" ? t("Canceled. Refresh the library before retrying.", "已取消，請先重新整理清單再重試。") :
                        publicationErrorMessage(err.response?.data?.code, language) || err.response?.data?.error) ||
                    err.message ||
                    t("Unknown error", "未知錯誤");
                setQueue((prev) =>
                    prev.map((q) =>
                        q.id === item.id
                            ? { ...q, status: "error", error: msg }
                            : q,
                    ),
                );
            } finally {
                requestAbortRef.current = null;
            }
        }

        setIsRunning(false);
    };

    const handleStop = () => {
        abortRef.current = true;
        requestAbortRef.current?.abort();
    };

    const pendingCount = queue.filter((q) => q.status === "idle").length;
    const doneCount = queue.filter(
        (q) => q.status === "success" || q.status === "duplicate",
    ).length;
    const errorCount = queue.filter((q) => q.status === "error").length;
    const reviewCount = queue.filter((q) => q.status === "review").length;
    const finishedCount = doneCount + errorCount + reviewCount;

    return (
        <div className="flex justify-center">
            <div className="w-3/4 space-y-6">
                {/* Back */}
                <button
                    onClick={onBack}
                    className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-500 transition-colors font-medium"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    {t("Back to Upload Menu", "返回上傳選單")}
                </button>

                {/* Header */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                            <svg
                                className="w-5 h-5 text-red-500"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M23.498 6.186a2.994 2.994 0 00-2.107-2.117C19.399 3.5 12 3.5 12 3.5s-7.399 0-9.391.569A2.994 2.994 0 00.502 6.186 31.407 31.407 0 000 12a31.407 31.407 0 00.502 5.814 2.994 2.994 0 002.107 2.117C4.601 20.5 12 20.5 12 20.5s7.399 0 9.391-.569a2.994 2.994 0 002.107-2.117A31.407 31.407 0 0024 12a31.407 31.407 0 00-.502-5.814zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">
                                {t("Publish Videos", "發布影片")}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {t(
                                    "Add YouTube videos to make their content searchable by the Video Agent.",
                                    "新增 YouTube 影片，讓 Video Agent 可以搜尋影片內容。",
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* URL input */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-3">
                    <label className="block text-sm font-semibold text-gray-700">
                        {t("YouTube URL", "YouTube 網址")}
                    </label>
                    <div className="flex gap-3">
                        <input
                            type="url"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="https://www.youtube.com/watch?v=..."
                            disabled={isRunning}
                            className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all text-sm disabled:opacity-50"
                        />
                        <button
                            onClick={handleAdd}
                            disabled={!inputValue.trim() || isRunning}
                            className="px-5 py-2.5 bg-gray-800 hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all shadow-sm flex items-center gap-2"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            {t("Add", "新增")}
                        </button>
                    </div>
                </div>

                {/* Queue */}
                {queue.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        {/* Queue header */}
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
                            <div className="flex items-center gap-4 text-xs font-medium text-gray-500">
                                <span>
                                    {t("Queue", "待處理列表")} ({queue.length})
                                </span>
                                {doneCount > 0 && (
                                    <span className="text-green-600">
                                        ✓ {doneCount} {t("done", "完成")}
                                    </span>
                                )}
                                {errorCount > 0 && (
                                    <span className="text-red-500">
                                        ✗ {errorCount} {t("failed", "失敗")}
                                    </span>
                                )}
                                {reviewCount > 0 && (
                                    <span className="text-amber-600">
                                        {reviewCount} {t("needs review", "待審核")}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {finishedCount > 0 && !isRunning && (
                                    <button
                                        onClick={handleClearDone}
                                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors px-2 py-1 rounded-lg hover:bg-gray-100"
                                    >
                                        {t("Clear finished", "清除已完成")}
                                    </button>
                                )}
                                {!isRunning ? (
                                    <button
                                        onClick={handleStart}
                                        disabled={pendingCount === 0}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-all shadow-sm"
                                    >
                                        {t(
                                            `Upload (${pendingCount})`,
                                            `上傳 (${pendingCount})`,
                                        )}
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleStop}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded-lg transition-all shadow-sm"
                                    >
                                        <svg
                                            className="w-3.5 h-3.5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9 10h6v4H9z"
                                            />
                                        </svg>
                                        {t("Stop", "停止")}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* URL rows */}
                        <div className="p-4 space-y-2 max-h-[420px] overflow-y-auto">
                            {queue.map((item) => (
                                <UrlRow
                                    key={item.id}
                                    item={item}
                                    language={language}
                                    onRemove={handleRemove}
                                />
                            ))}
                        </div>

                        {/* Progress bar */}
                        {isRunning && (
                            <div className="px-4 pb-4">
                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                    <div
                                        className="bg-orange-500 h-1.5 rounded-full transition-all duration-500"
                                        style={{
                                            width: `${(finishedCount / queue.length) * 100}%`,
                                        }}
                                    />
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1 text-right">
                                    {finishedCount} / {queue.length}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Empty state */}
                {queue.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-4 text-center text-gray-400">
                        <svg
                            className="w-12 h-12 mb-3 text-gray-200"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M15 10l4.553-2.07A2 2 0 0122 9.79v4.42a2 2 0 01-2.447 1.86L15 14M3 8h12a2 2 0 012 2v4a2 2 0 01-2 2H3a2 2 0 01-2-2V10a2 2 0 012-2z"
                            />
                        </svg>
                        <p className="text-sm font-medium">
                            {t("No URLs added yet", "尚未新增網址")}
                        </p>
                        <p className="text-xs mt-1">
                            {t(
                                "Paste one or more YouTube links above",
                                "請在上方貼上一個或多個 YouTube 連結",
                            )}
                        </p>
                    </div>
                )}
                <VideoLibrary key={libraryState.revision} language={language} addedIds={libraryState.addedIds} />
            </div>
        </div>
    );
}

export default PublishVideos;
